import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { requestsApi, startPointsApi, StartPoint, patientsApi } from '../../services/api';
import { getDefaultReadyTimeUTC } from '../../utils/timeUtils';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useScrollToError } from '../../hooks/useScrollToError';
import { useNavigationBlock } from '../../hooks/useNavigationBlock';
import { Paperclip, X, ArrowLeft, AlertTriangle } from 'lucide-react';
import LeaveConfirmDialog from '../../components/LeaveConfirmDialog/LeaveConfirmDialog';
import HelpTooltip from '../../components/HelpTooltip/HelpTooltip';
import './CreateRequest.css';

const DRAFT_STORAGE_KEY = 'camo-create-request-draft';

function CreateRequest() {
  const { t } = useTranslation();
  const { user, effectiveRoles } = useAuth();
  const navigate = useNavigate();

  // Load draft from localStorage if available
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          title: parsed.title || '',
          description: parsed.description || '',
          category: parsed.category || 'MEDEVAC',
          urgency: parsed.urgency || 'NORMAL',
          exitPointId: parsed.exitPointId || '',
          readyAtHub: parsed.readyAtHub || getDefaultReadyTimeUTC(),
          medevacRemarks: parsed.medevacRemarks || '',
        };
      }
    } catch { /* ignore corrupt data */ }
    return null;
  };

  const [formData, setFormData] = useState(loadDraft() || {
    title: '',
    description: '',
    category: 'MEDEVAC' as 'PERSONNEL' | 'MATERIAL' | 'MEDEVAC' | 'TRANSPORT' | 'OTHER' | 'NEWS',
    urgency: 'NORMAL' as 'HIGH' | 'NORMAL',
    exitPointId: '',
    readyAtHub: getDefaultReadyTimeUTC(),
    medevacRemarks: ''
  });
  const [hasDraft, setHasDraft] = useState(!!loadDraft());
  const autoSaveTimerRef = useRef<NodeJS.Timeout>();

  // Auto-save draft every 30 seconds
  useEffect(() => {
    autoSaveTimerRef.current = setInterval(() => {
      if (formData.title || formData.description) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
      }
    }, 30000);
    return () => {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    };
  }, [formData]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
  }, []);

  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useScrollToError(error);
  const [availableStartPoints, setAvailableStartPoints] = useState<StartPoint[]>([]);
  const [startPointsLoaded, setStartPointsLoaded] = useState(false);
  const [previewPatients, setPreviewPatients] = useState<any[]>([]);
  const [previewErrors, setPreviewErrors] = useState<Array<{ row: number | string; error?: string; message?: string; level?: 'ERROR' | 'WARN' }>>([]);
  const [previewSummary, setPreviewSummary] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState<'draft' | 'submit' | null>(null);
  const [showSkippedDetails, setShowSkippedDetails] = useState(false);
  const [showWarningDetails, setShowWarningDetails] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const pendingProceedRef = useRef<(() => void) | null>(null);

  // Block all navigation (React Router links, back button, tab close) when form is dirty
  useNavigationBlock(isDirty && !loading, useCallback((proceed: () => void) => {
    pendingProceedRef.current = proceed;
    setShowLeaveDialog(true);
  }, []));

  useEffect(() => {
    loadAvailableStartPoints();
  }, []);

  const loadAvailableStartPoints = async () => {
    try {
      if (user?.nation) {
        const response = await startPointsApi.getAll({ nation: user.nation });
        setAvailableStartPoints(response.data);
      }
    } catch (err) {
      console.error('Failed to load available hubs:', err);
    } finally {
      setStartPointsLoaded(true);
    }
  };

  // Only allow nations, EU, EXCON, and ADMIN to create requests
  if (!(effectiveRoles.isNation || effectiveRoles.isEU || effectiveRoles.isExcon || effectiveRoles.isAdmin)) {
    return (
      <div className="error-page">
        <h2>{t('common.error')}</h2>
        <p>{t('common.pageNotFound')}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          {t('common.backToDashboard')}
        </button>
      </div>
    );
  }

  const canCreateNews = effectiveRoles.isEU || effectiveRoles.isExcon || effectiveRoles.isAdmin;

  // Block MEDEVAC creation if no exit hubs are available for the user's nation
  const noExitHubs = startPointsLoaded && availableStartPoints.length === 0;
  const isMedevacBlocked = formData.category === 'MEDEVAC' && noExitHubs;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/)) {
        setError('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }

      // Validate file size (10 MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10 MB');
        return;
      }

      setExcelFile(file);
      setError('');
      setIsDirty(true);

      // Reset previous preview (Edge case #1: New file invalidates old preview)
      setShowPreview(false);
      setPreviewPatients([]);
      setPreviewErrors([]);
      setPreviewSummary(null);
      setShowImportConfirm(null);

      // For MEDEVAC: Load patient preview
      console.log('Category:', formData.category, 'File:', file.name);
      if (formData.category === 'MEDEVAC') {
        console.log('Loading preview for MEDEVAC request...');
        setLoading(true);
        try {
          const patientFormData = new FormData();
          patientFormData.append('patientExcel', file);

          // NEW: Parse Excel and get preview (without saving)
          const response = await patientsApi.previewExcel(patientFormData);

          setPreviewPatients(response.data.patients);
          setPreviewErrors(response.data.errors);
          setPreviewSummary(response.data.summary);
          setShowPreview(true);

          // Scroll to preview section
          setTimeout(() => {
            document.getElementById('patient-preview')?.scrollIntoView({ behavior: 'smooth' });
          }, 100);

        } catch (err: any) {
          const errorMessage = err.response?.data?.error || 'Failed to parse Excel file';
          setError(errorMessage);
          setShowPreview(false);

          // Show alert for critical errors (file size, patient limit, etc.)
          alert(`Excel Upload Error\n\n${errorMessage}`);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Validate total number of files
    if (attachments.length + files.length > 5) {
      setError('Maximum 5 files allowed');
      return;
    }

    // Validate each file
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/pdf',
      'application/zip',
      'application/x-zip-compressed'
    ];

    const validExtensions = /\.(xlsx?|docx?|pdf|zip)$/i;

    for (const file of files) {
      // Validate file type
      if (!validTypes.includes(file.type) && !validExtensions.test(file.name)) {
        setError(`File ${file.name}: Only Excel (.xls, .xlsx), Word (.doc, .docx), PDF (.pdf), and ZIP (.zip) files are allowed`);
        return;
      }

      // Validate file size (5 MB per file)
      if (file.size > 5 * 1024 * 1024) {
        setError(`File ${file.name}: Maximum file size is 5 MB`);
        return;
      }
    }

    setAttachments(prev => [...prev, ...files]);
    setError('');

    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    // Reset input so the same file can be re-selected
    const input = document.getElementById('attachments') as HTMLInputElement;
    if (input) input.value = '';
  };

  const recalculatePreviewSummary = (patients: any[]) => {
    return {
      total: patients.length,
      civil: patients.filter((p: any) => p.patientStatus === 'CIVIL').length,
      military: patients.filter((p: any) => p.patientStatus === 'MILITARY').length,
      p1: patients.filter((p: any) => p.medicalPriority === 'P1').length,
      p2: patients.filter((p: any) => p.medicalPriority === 'P2').length,
      p3: patients.filter((p: any) => p.medicalPriority === 'P3').length,
      seated: patients.filter((p: any) => p.medicalTransport === 'SEATED').length,
      lying: patients.filter((p: any) => p.medicalTransport === 'LYING').length,
      withCompanions: patients.filter((p: any) => (p.patientCompanionsCount || 0) > 0).length,
      errors: 0,
      warnings: 0
    };
  };

  const handleRemovePatient = (index: number) => {
    const updated = previewPatients.filter((_, i) => i !== index);
    setPreviewPatients(updated);
    setPreviewSummary(recalculatePreviewSummary(updated));
  };

  const handleSubmit = async (e: React.FormEvent, submitForReview: boolean = false, skipConfirm: boolean = false) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.title?.trim()) {
      setError('Title is required');
      return;
    }

    // Validation for MEDEVAC requests (before loading state)
    if (formData.category === 'MEDEVAC') {
      // Require Excel file
      if (!excelFile) {
        setError('Excel file is required for MEDEVAC requests');
        return;
      }

      // Require preview to be shown (Edge case #3: User must review before submit)
      if (!showPreview || previewPatients.length === 0) {
        setError('Please upload an Excel file and review the patient preview before submitting');
        return;
      }

      // Require Exit Point selection
      if (!formData.exitPointId) {
        setError('Please select a UCPM Hub for MEDEVAC requests');
        return;
      }

      // Show confirmation popup if there are warnings/skipped patients
      if (previewErrors.length > 0 && !skipConfirm) {
        setShowImportConfirm(submitForReview ? 'submit' : 'draft');
        return;
      }
    }
    setLoading(true);

    try {

      let requestId: string;

      // NEW: Create request with Excel file in one step (MEDEVAC only)
      if (formData.category === 'MEDEVAC' && excelFile) {
        const response = await requestsApi.createWithExcel(
          {
            title: formData.title,
            description: formData.description,
            category: formData.category,
            urgency: formData.urgency,
            readyAtHub: formData.readyAtHub || undefined,
            medevacRemarks: formData.medevacRemarks || undefined
          },
          excelFile
        );

        requestId = response.data.request.id;

        // Set Exit Point if selected
        if (formData.exitPointId) {
          await patientsApi.updateExitPoint(requestId, formData.exitPointId);
        }

        // Show import feedback if available
        if (response.data.patients) {
          console.log(`Imported ${response.data.patients.imported} patients`);
          if (response.data.patients.failed > 0) {
            console.warn(`Failed to import ${response.data.patients.failed} patients`);
          }
        }
      } else {
        // Create request with attachments (for non-MEDEVAC categories)
        const response = await requestsApi.createWithAttachments(
          {
            title: formData.title,
            description: formData.description,
            category: formData.category,
            urgency: formData.urgency
          },
          attachments
        );

        requestId = response.data.request.id;
      }

      // Submit for review if requested
      if (submitForReview) {
        await requestsApi.submit(requestId);
      }

      // Clear draft and navigate to request detail
      setIsDirty(false);
      clearDraft();
      navigate(`/requests/${requestId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowLeaveDialog(true);
    } else {
      navigate('/');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('/docs/template_evacuations_request.xlsx');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_evacuations_request.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // fallback: direct link
      window.location.href = '/docs/template_evacuations_request.xlsx';
    }
  };

  return (
    <div className="create-request-container">
        <div className="create-request-main">
          <div className="page-header">
            <h1>{t('requests.createRequest')}</h1>
            <p>{t('requests.requestDescription')}</p>
          </div>

          {error && <div ref={errorRef} className="error">{error}</div>}

          {isMedevacBlocked && (
            <div className="no-exit-hub-warning" role="alert">
              <AlertTriangle size={20} />
              <div>
                <strong>No exit hubs available</strong>
                <p>Your nation does not have any access to a UCPM TRANSIT hub. Please contact EXCON to set up a UCPM transit hub before creating a MEDEVAC request.</p>
              </div>
            </div>
          )}

          <form className="request-form card" style={{ position: 'relative' }}>
            <LoadingOverlay loading={loading} text={loading ? 'Processing...' : undefined} />
            <p className="form-required-legend"><span className="required">*</span> Required field</p>
        <div className="form-section">
          <h3>{t('requests.generalInfo')}</h3>

          <div className="form-group">
            <label htmlFor="title">
              {t('requests.requestTitle')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g. MEDEVAC Request - 12 patients from Region Alpha"
              required
              maxLength={200}
              className={!formData.title.trim() && error ? 'has-error' : ''}
            />
            <div className="form-field-footer">
              <small className="form-hint">A clear, descriptive title helps responders understand your request quickly</small>
              <span className={`char-counter ${formData.title.length > 180 ? 'char-counter--warning' : ''}`}>
                {formData.title.length}/200
              </span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {t('requests.description')} <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the situation, number of patients/resources needed, special requirements, and any relevant context..."
              required
              rows={6}
            />
            <div className="form-field-footer">
              <small className="form-hint">Include situation details, resource requirements, and any special considerations</small>
              {formData.description.length > 0 && (
                <span className="char-counter">{formData.description.length} chars</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                {t('requests.category')} <span className="required">*</span> <HelpTooltip text="MEDEVAC: Medical evacuation. PERSONNEL: Staff requests. MATERIAL: Equipment/supplies. OTHER: Miscellaneous assistance." />
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="PERSONNEL">{t('categories.personnel')}</option>
                <option value="MATERIAL">{t('categories.equipment')}</option>
                <option value="MEDEVAC">{t('categories.medevac')} (Medical Evacuation)</option>
                <option value="OTHER">{t('categories.other')}</option>
                {canCreateNews && <option value="NEWS">{t('categories.news')}</option>}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="urgency">
                {t('requests.urgency')} <span className="required">*</span> <HelpTooltip text="HIGH: Immediate attention needed, prioritized processing. NORMAL: Standard processing timeline." />
              </label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleInputChange}
                required
              >
                <option value="NORMAL">{t('urgency.routine')}</option>
                <option value="HIGH">{t('urgency.urgent')}</option>
              </select>
            </div>
          </div>

          {formData.category === 'MEDEVAC' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="exitPointId">
                    UCPM TRANSIT HUB<span className="required">*</span> <HelpTooltip text="The UCPM Exit Hub where patients will be evacuated from. This is typically an airport or border crossing point." />
                  </label>
                  <select
                    id="exitPointId"
                    name="exitPointId"
                    value={formData.exitPointId}
                    onChange={handleInputChange}
                    required={formData.category === 'MEDEVAC'}
                  >
                    <option value="">Select UCPM Hub...</option>
                    {availableStartPoints.map((startPoint) => (
                      <option key={startPoint.id} value={startPoint.id}>
                        {startPoint.name} ({startPoint.nation}) - {startPoint.types.map(t => t.replace('_', ' ')).join(', ')}
                      </option>
                    ))}
                  </select>
                  {availableStartPoints.length === 0 && (
                    <small className="warning-text">
                      No hubs available in your nation. Please contact EXCON.
                    </small>
                  )}
                  {availableStartPoints.length > 0 && (
                    <small>Select the UCPM TRANSIT HUB where patients will be ready for pickup</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="readyAtHub">
                    Ready at UCPM TRANSIT HUB (UTC) <span className="required">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="readyAtHub"
                    name="readyAtHub"
                    value={formData.readyAtHub}
                    onChange={handleInputChange}
                    required={formData.category === 'MEDEVAC'}
                  />
                  <small>Enter time in UTC. Default: Current UTC time + 36 hours (rounded to next 30 minutes)</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="medevacRemarks">
                  MEDEVAC Remarks
                </label>
                <textarea
                  id="medevacRemarks"
                  name="medevacRemarks"
                  value={formData.medevacRemarks}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Additional remarks or special requirements for the MEDEVAC operation..."
                />
                <small>Optional: Any additional information for the transporting nation</small>
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h3>{t('requests.additionalInfo')}</h3>

          {formData.category === 'MEDEVAC' ? (
            <div className="form-group">
              <label htmlFor="excelFile">
                Excel File
                <span className="required">*</span>
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="excelFile"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  required
                />
                {excelFile && (
                  <div className="file-info">
                    <span className="file-name"><Paperclip size={14} /> {excelFile.name}</span>
                    <span className="file-size">
                      ({(excelFile.size / 1024).toFixed(1)} KB)
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setExcelFile(null);
                        setShowPreview(false);
                        setPreviewPatients([]);
                        setPreviewErrors([]);
                        setPreviewSummary(null);
                        // Reset the file input so the same file can be re-selected
                        const fileInput = document.getElementById('excelFile') as HTMLInputElement;
                        if (fileInput) fileInput.value = '';
                      }}
                      className="btn-remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
              <small>Excel file with patient data is required for MEDEVAC requests</small>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="attachments">
                Attachments
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="attachments"
                  accept=".xlsx,.xls,.docx,.doc,.pdf,.zip"
                  onChange={handleAttachmentChange}
                  multiple
                />
              </div>
              <small>
                Upload supporting documents (Excel, Word, PDF, ZIP). Maximum 5 files, 5 MB each.
              </small>

              {attachments.length > 0 && (
                <div className="attachments-list">
                  {attachments.map((file, index) => (
                    <div key={index} className="file-info">
                      <span className="file-name"><Paperclip size={14} /> {file.name}</span>
                      <span className="file-size">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="btn-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {formData.category === 'MEDEVAC' && (
            <div style={{ marginTop: '8px' }}>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setShowHelpModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                &#x2753; MEDEVAC Excel Help &amp; Template
              </button>
            </div>
          )}

          {/* Patient Preview with Statistics for MEDEVAC */}
          {formData.category === 'MEDEVAC' && showPreview && (
            <div className="preview-section" id="patient-preview">
              {/* Compact Statistics Bar */}
              {previewSummary && (
                <div className="preview-summary-bar">
                  <span className="stat-inline"><strong>{previewSummary.total}</strong> patients</span>
                  <span className="stat-sep">|</span>
                  <span className="stat-inline priority-p1">P1: <strong>{previewSummary.p1}</strong></span>
                  <span className="stat-inline priority-p2">P2: <strong>{previewSummary.p2}</strong></span>
                  <span className="stat-inline priority-p3">P3: <strong>{previewSummary.p3}</strong></span>
                  <span className="stat-sep">|</span>
                  <span className="stat-inline">Seated: <strong>{previewSummary.seated}</strong></span>
                  <span className="stat-inline">Lying: <strong>{previewSummary.lying}</strong></span>
                  <span className="stat-sep">|</span>
                  <span className="stat-inline">Companions: <strong>{previewSummary.withCompanions}</strong></span>
                  {previewSummary.military > 0 && (
                    <>
                      <span className="stat-sep">|</span>
                      <span className="stat-inline">Military: <strong>{previewSummary.military}</strong></span>
                    </>
                  )}
                </div>
              )}

              {/* Parsing Warnings Display (skipped patients & default values) */}
              {previewErrors.length > 0 && (() => {
                const skipped = previewErrors.filter(e => (e.message || e.error || '').startsWith('Skipped:'));
                const warnings = previewErrors.filter(e => !(e.message || e.error || '').startsWith('Skipped:'));
                const passed = previewPatients.length;
                return (
                  <div className="preview-errors">
                    <div className="error-summary-bar">
                      {skipped.length > 0 && (
                        <button
                          type="button"
                          className={`error-summary-chip chip-danger ${showSkippedDetails ? 'active' : ''}`}
                          onClick={() => setShowSkippedDetails(!showSkippedDetails)}
                        >
                          {skipped.length} Skipped
                        </button>
                      )}
                      {warnings.length > 0 && (
                        <button
                          type="button"
                          className={`error-summary-chip chip-warning ${showWarningDetails ? 'active' : ''}`}
                          onClick={() => setShowWarningDetails(!showWarningDetails)}
                        >
                          {warnings.length} Warning{warnings.length > 1 ? 's' : ''}
                        </button>
                      )}
                      <span className="error-summary-chip chip-success">
                        {passed} Passed
                      </span>
                    </div>
                    {showSkippedDetails && skipped.length > 0 && (
                      <div className="error-details-list">
                        {skipped.map((err, idx) => (
                          <div key={`skip-${idx}`} className="error-detail-item error-detail-danger">
                            <strong>Row {err.row}:</strong> {err.message}
                          </div>
                        ))}
                      </div>
                    )}
                    {showWarningDetails && warnings.length > 0 && (
                      <div className="error-details-list">
                        {warnings.map((err, idx) => (
                          <div key={`warn-${idx}`} className="error-detail-item error-detail-warning">
                            <strong>Row {err.row}:</strong> {err.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Patient Table Preview */}
              {previewPatients.length > 0 && (
                <div className="preview-patients">
                  <h4>Patient Preview ({previewPatients.length})</h4>
                  <p className="preview-instruction">
                    Review all patients before submitting. Remove incorrect entries with the X button.
                  </p>
                  <div className="patients-table-wrapper">
                    <table className="patients-overview-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Initials</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>Status</th>
                          <th>Nationality</th>
                          <th>Priority</th>
                          <th>Diagnosis</th>
                          <th>Transport</th>
                          <th>Dependency</th>
                          <th>Requirements</th>
                          <th>Companions</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewPatients.map((patient: any, idx: number) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td><strong>{patient.patientInitials || '-'}</strong></td>
                            <td>{patient.patientAge || '-'}</td>
                            <td>{patient.patientGender || '-'}</td>
                            <td>
                              <span className={`badge badge-${patient.patientStatus?.toLowerCase() || 'default'}`}>
                                {patient.patientStatus || '-'}
                              </span>
                            </td>
                            <td>{patient.nationality || '-'}</td>
                            <td>
                              <span className={`badge badge-priority-${patient.medicalPriority?.toLowerCase() || 'default'}`}>
                                {patient.medicalPriority || '-'}
                              </span>
                            </td>
                            <td>{patient.medicalPrimaryConditionDiagnosis || '-'}</td>
                            <td>{patient.medicalTransport || '-'}</td>
                            <td>{patient.medicalDependency || '-'}</td>
                            <td>
                              {patient.medicalRequirements && patient.medicalRequirements.length > 0 ? (
                                <div className="requirements-list">
                                  {patient.medicalRequirements.map((req: string, i: number) => (
                                    <span key={i} className="requirement-tag">
                                      {req.replace('_', ' ')}
                                    </span>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              {patient.patientCompanionsCount > 0 ? (
                                <div>
                                  <strong>{patient.patientCompanionsCount}x</strong>
                                  {patient.patientCompanionsDetails && (
                                    <div className="companion-details">
                                      {patient.patientCompanionsDetails}
                                    </div>
                                  )}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn-remove-patient"
                                onClick={() => handleRemovePatient(idx)}
                                title="Remove patient"
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            className="btn btn-outline"
            disabled={loading || isMedevacBlocked}
          >
            {loading ? t('requests.saving') : t('requests.saveDraft')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            className="btn btn-primary"
            disabled={loading || isMedevacBlocked}
          >
            {loading ? t('requests.saving') : t('requests.submitForReview')}
          </button>
        </div>
      </form>
      </div>

      {/* MEDEVAC Help Modal */}
      {showHelpModal && (
        <div className="confirm-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>MEDEVAC Excel Import Guide</h2>
              <button className="close-btn" onClick={() => setShowHelpModal(false)}><X size={18} /></button>
            </div>
            <div className="help-modal-body">
              <p className="help-intro">
                Upload an Excel file (.xlsx) with patient data. Each row = one patient. Max <strong>500</strong> patients.
              </p>
              <div className="help-callout">
                <strong>Missing columns</strong> reject the entire file. <strong>Invalid rows</strong> skip only those patients.
              </div>
              <div className="help-table-scroll">
                <table className="help-fields-table">
                  <thead>
                    <tr>
                      <th>Column Name</th>
                      <th>Description</th>
                      <th>Impact if Missing/Invalid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* RED - Import stop / patient skipped */}
                    <tr className="help-field-critical">
                      <td><strong>Last Name</strong></td>
                      <td>Patient's last name (sensitive, only visible to Admin/ExCon)</td>
                      <td><span className="help-badge help-badge-red">Patient Skipped</span></td>
                    </tr>
                    <tr className="help-field-critical">
                      <td><strong>First Name</strong></td>
                      <td>Patient's first name (sensitive, only visible to Admin/ExCon)</td>
                      <td><span className="help-badge help-badge-red">Patient Skipped</span></td>
                    </tr>
                    <tr className="help-field-critical">
                      <td><strong>Primary Diagnosis</strong></td>
                      <td>Medical diagnosis description</td>
                      <td><span className="help-badge help-badge-red">Patient Skipped</span></td>
                    </tr>
                    <tr className="help-field-critical">
                      <td><strong>Birth Date + Age</strong></td>
                      <td>Birth Date (DD.MM.YYYY, sensitive) and/or Age (years). At least one must be present.</td>
                      <td><span className="help-badge help-badge-red">Patient Skipped</span> (if both missing)</td>
                    </tr>

                    {/* YELLOW-ORANGE - Warning, imported with defaults */}
                    <tr className="help-field-warn">
                      <td><strong>Gender</strong></td>
                      <td>M (Male), F (Female), or D (Diverse)</td>
                      <td><span className="help-badge help-badge-orange">Default: TBA</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Status</strong></td>
                      <td>CIVIL or MILITARY</td>
                      <td><span className="help-badge help-badge-orange">Default: TBA</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Nationality</strong></td>
                      <td>ISO 3-letter country code (e.g., DEU, FRA, LTU)</td>
                      <td><span className="help-badge help-badge-orange">Default: empty</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>ICD-10 Block</strong></td>
                      <td>ICD-10 code for diagnosis classification</td>
                      <td><span className="help-badge help-badge-orange">Default: ---</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Position</strong></td>
                      <td>Transport position: SEATED, LYING, or TBD (TBD mapped to LYING)</td>
                      <td><span className="help-badge help-badge-orange">Default: TBA</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Dependency Level</strong></td>
                      <td>Level 1, Level 2, Level 3, or Level 4</td>
                      <td><span className="help-badge help-badge-orange">Default: TBA</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Priority</strong></td>
                      <td>P1 (highest), P2 (medium), or P3 (lowest)</td>
                      <td><span className="help-badge help-badge-orange">Default: TBA</span></td>
                    </tr>
                    <tr className="help-field-warn">
                      <td><strong>Further Treatment</strong></td>
                      <td>Required treatment types: Intensive Care, Surgery, Oncology, Dialysis, Burn Unit, Nursing, Maternity (comma-separated)</td>
                      <td><span className="help-badge help-badge-orange">Default: none</span></td>
                    </tr>

                    {/* GREEN - Optional, silent defaults */}
                    <tr className="help-field-optional">
                      <td><strong>Accompanying Number</strong></td>
                      <td>Number of companions (0 if none)</td>
                      <td><span className="help-badge help-badge-green">Optional (default: 0)</span></td>
                    </tr>
                    <tr className="help-field-optional">
                      <td><strong>Accompanying Details</strong></td>
                      <td>Description of companions</td>
                      <td><span className="help-badge help-badge-green">Optional</span></td>
                    </tr>
                    <tr className="help-field-optional">
                      <td><strong>Notes</strong></td>
                      <td>Additional remarks for the patient</td>
                      <td><span className="help-badge help-badge-green">Optional</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="help-footer">
                <div className="help-legend">
                  <span className="help-badge help-badge-red">Required</span> = patient skipped
                  <span className="help-badge help-badge-orange">Default</span> = imported with default
                  <span className="help-badge help-badge-green">Optional</span> = no impact
                </div>
                <button onClick={handleDownloadTemplate} className="btn btn-primary btn-sm">
                  Download Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Confirmation Popup */}
      {showImportConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3>Confirm Patient Import</h3>
            <div className="confirm-warning">
              {(() => {
                const skipped = previewErrors.filter(e => (e.message || e.error || '').startsWith('Skipped:'));
                const warnings = previewErrors.filter(e => !(e.message || e.error || '').startsWith('Skipped:'));
                return (
                  <>
                    {skipped.length > 0 && (
                      <p><strong>{skipped.length} patient(s) will be skipped</strong> due to missing required fields.</p>
                    )}
                    {warnings.length > 0 && (
                      <p><strong>{warnings.length} field(s)</strong> were imported with default values.</p>
                    )}
                    <p>Only <strong>{previewPatients.length} patient(s)</strong> will be imported.</p>
                  </>
                );
              })()}
              <p style={{ marginTop: '12px' }}>Do you want to continue?</p>
            </div>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowImportConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  const submitForReview = showImportConfirm === 'submit';
                  setShowImportConfirm(null);
                  handleSubmit(e, submitForReview, true);
                }}
              >
                Yes, Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showLeaveDialog && (
        <LeaveConfirmDialog
          onSaveAndLeave={() => {
            localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
            setIsDirty(false);
            setShowLeaveDialog(false);
            if (pendingProceedRef.current) {
              pendingProceedRef.current();
              pendingProceedRef.current = null;
            } else {
              navigate('/');
            }
          }}
          onDiscardAndLeave={() => {
            clearDraft();
            setIsDirty(false);
            setShowLeaveDialog(false);
            if (pendingProceedRef.current) {
              pendingProceedRef.current();
              pendingProceedRef.current = null;
            } else {
              navigate('/');
            }
          }}
          onStay={() => {
            setShowLeaveDialog(false);
            pendingProceedRef.current = null;
          }}
        />
      )}
    </div>
  );
}

export default CreateRequest;
